import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PurchaseOrderDetailSchema,
  PurchaseRequestDetailSchema,
  PurchaseRequestSchema,
  SupplierSchema,
  type PurchaseOrderDetailDto,
  type PurchaseRequestDetailDto,
  type PurchaseRequestDto,
  type SupplierDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { ApiError, getData, newIdempotencyKey, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordFailure } from '../finance/detail';
import { SearchPicker, SupplierPicker } from '../finance/lookups';
import {
  dimensionText,
  EDITABLE,
  emptyTarget,
  loadTarget,
  OriginChip,
  PO_API,
  PO_TYPE,
  PR_API,
  TargetFields,
  targetPayload,
  targetProblems,
  useBusinessToday,
  type TargetDraft,
} from './purchasing';

type Origin = 'purchase_request' | 'emergency';

/** The target a converted line carries over from its request line, shown and kept as it is. */
interface CarriedTarget {
  readonly accountId: string;
  readonly accountLabel: string | null;
  readonly costCenterId: string | null;
  readonly projectId: string | null;
  readonly assetId: string | null;
  readonly dimensions: string;
}

interface OrderLineDraft {
  readonly key: number;
  include: boolean;
  purchaseRequestLineId: string | null;
  source: string | null;
  description: string;
  quantity: string;
  unitPrice: string;
  carried: CarriedTarget | null;
  target: TargetDraft;
}

const DECIMAL = /^\d{1,15}(\.\d{1,4})?$/;

const emptyLine = (key: number): OrderLineDraft => ({
  key,
  include: true,
  purchaseRequestLineId: null,
  source: null,
  description: '',
  quantity: '',
  unitPrice: '',
  carried: null,
  target: emptyTarget(),
});

function carriedFrom(line: {
  accountId: string;
  accountLabel: string | null;
  costCenterId: string | null;
  costCenterLabel: string | null;
  projectId: string | null;
  projectLabel: string | null;
  assetId: string | null;
  assetLabel: string | null;
}): CarriedTarget {
  return {
    accountId: line.accountId,
    accountLabel: line.accountLabel,
    costCenterId: line.costCenterId,
    projectId: line.projectId,
    assetId: line.assetId,
    dimensions: dimensionText(line),
  };
}

/** Lines from an approved request's remaining quantities (FRD-PRC-024). */
function linesFromRequest(request: PurchaseRequestDetailDto): OrderLineDraft[] {
  return request.lines
    .filter((l) => !/^0*(\.0*)?$/.test(l.remainingQuantity))
    .map((l, index) => ({
      key: index + 1,
      include: true,
      purchaseRequestLineId: l.id,
      source: `Request line ${String(l.lineNo)} · ${l.remainingQuantity} left to order`,
      description: l.description,
      quantity: l.remainingQuantity,
      unitPrice: l.estimatedUnitCost,
      carried: carriedFrom(l),
      target: emptyTarget(),
    }));
}

function problemsOf(lines: readonly OrderLineDraft[], origin: Origin): string[] {
  const included = lines.filter((l) => l.include);
  if (included.length === 0) return ['Include at least one line.'];
  return included.flatMap((line, index) => {
    const n = `Line ${String(index + 1)}`;
    const out: string[] = [];
    if (line.description.trim() === '') out.push(`${n}: describe the item or service.`);
    if (!DECIMAL.test(line.quantity.trim()) || /^0*(\.0*)?$/.test(line.quantity.trim())) {
      out.push(`${n}: enter a quantity above zero (up to 4 decimals).`);
    }
    if (!DECIMAL.test(line.unitPrice.trim())) out.push(`${n}: enter the unit price.`);
    if (origin === 'emergency' && line.carried === null) {
      out.push(...targetProblems(line.target, n));
    }
    return out;
  });
}

function linePayload(line: OrderLineDraft) {
  const target =
    line.carried === null
      ? targetPayload(line.target)
      : {
          accountId: line.carried.accountId,
          ...(line.carried.costCenterId !== null
            ? { costCenterId: line.carried.costCenterId }
            : {}),
          ...(line.carried.projectId !== null ? { projectId: line.carried.projectId } : {}),
          ...(line.carried.assetId !== null ? { assetId: line.carried.assetId } : {}),
        };
  return {
    ...(line.purchaseRequestLineId !== null
      ? { purchaseRequestLineId: line.purchaseRequestLineId }
      : {}),
    description: line.description.trim(),
    quantity: line.quantity.trim(),
    unitPrice: line.unitPrice.trim(),
    ...target,
  };
}

const describeRequest = (r: PurchaseRequestDto) =>
  `${r.documentNo} — ${r.requesterLabel ?? ''} — ${r.justification}`;

/**
 * `/procurement/purchase-orders/new`: an order from an approved request (`?request=<id>`), whose
 * lines and quantities Procurement chooses, or the controlled emergency exception
 * (`?emergency=1`) with its mandatory bypass reason (FRD-PRC-024, PRC-025).
 */
export function NewPurchaseOrderPage() {
  const [params, setParams] = useSearchParams();
  const session = useSession();
  const requestId = params.get('request');
  const origin: Origin = params.get('emergency') === '1' ? 'emergency' : 'purchase_request';
  const request = useQuery({
    queryKey: ['procurement', 'purchase-request', requestId],
    enabled: requestId !== null,
    queryFn: () => getData(`${PR_API}/${requestId ?? ''}`, PurchaseRequestDetailSchema),
  });
  const back = (
    <Button component={RouterLink} to="/procurement/purchase-orders" startIcon={<ArrowBackIcon />}>
      All purchase orders
    </Button>
  );
  if (!hasCapability(session.data, `${PO_TYPE}:create`)) {
    return (
      <Stack spacing={3}>
        <PageHeader title="New purchase order" actions={back} />
        <Alert severity="info">You do not have permission to raise purchase orders.</Alert>
      </Stack>
    );
  }
  const chooser = (
    <Section title="Origin">
      <Stack spacing={2} sx={{ p: 2.5 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={origin}
          aria-label="Origin"
          onChange={(_, next: Origin | null) => {
            if (next === null) return;
            setParams(next === 'emergency' ? { emergency: '1' } : {});
          }}
        >
          <ToggleButton value="purchase_request">From an approved purchase request</ToggleButton>
          <ToggleButton value="emergency">Emergency exception</ToggleButton>
        </ToggleButtonGroup>
        {origin === 'purchase_request' && (
          <SearchPicker<PurchaseRequestDto>
            label="Approved purchase request"
            queryKey="purchase-requests-approved"
            schema={PurchaseRequestSchema}
            value={request.data?.request ?? null}
            path={(term) =>
              `${PR_API}?pageSize=25&sort=-document_no&filter[status]=approved` +
              (term === '' ? '' : `&filter[search]=${encodeURIComponent(term)}`)
            }
            describe={describeRequest}
            onChange={(next) => {
              setParams(next === null ? {} : { request: next.id });
            }}
          />
        )}
        {origin === 'emergency' && (
          <Alert severity="warning" variant="outlined">
            An emergency order bypasses the purchase request process. It needs a reason and its own
            approval, and it stays marked as an emergency order.
          </Alert>
        )}
      </Stack>
    </Section>
  );
  if (origin === 'purchase_request' && requestId !== null && request.isPending) {
    return <LoadingState label="Loading the purchase request" />;
  }
  if (origin === 'purchase_request' && requestId !== null && request.isError) {
    return (
      <RecordFailure
        title="New purchase order"
        noun="purchase request"
        error={request.error}
        back={back}
      />
    );
  }
  return (
    <Stack spacing={3}>
      <PageHeader title="New purchase order" actions={back} />
      {chooser}
      {origin === 'emergency' && (
        <OrderEditor key="emergency" origin="emergency" request={null} existing={null} />
      )}
      {origin === 'purchase_request' && request.data !== undefined && (
        <OrderEditor
          key={request.data.request.id}
          origin="purchase_request"
          request={request.data}
          existing={null}
        />
      )}
    </Stack>
  );
}

/** `/procurement/purchase-orders/:id/edit`: change a draft, returned or rejected order. */
export function EditPurchaseOrderPage() {
  const { id = '' } = useParams();
  const order = useQuery({
    queryKey: ['procurement', 'purchase-order', id],
    queryFn: () => getData(`${PO_API}/${id}`, PurchaseOrderDetailSchema),
  });
  const back = (
    <Button
      component={RouterLink}
      to={`/procurement/purchase-orders/${id}`}
      startIcon={<ArrowBackIcon />}
    >
      Back to the order
    </Button>
  );
  if (order.isPending) return <LoadingState label="Loading the purchase order" />;
  if (order.isError) {
    return (
      <RecordFailure title="Purchase order" noun="purchase order" error={order.error} back={back} />
    );
  }
  const o = order.data.order;
  return (
    <Stack spacing={3}>
      <PageHeader title={`Edit ${o.documentNo}`} actions={back} />
      {EDITABLE.includes(o.status) ? (
        <OrderEditor origin={o.origin} request={null} existing={order.data} />
      ) : (
        <Alert severity="info">A {o.status} purchase order cannot be edited.</Alert>
      )}
    </Stack>
  );
}

function OrderEditor({
  origin,
  request,
  existing,
}: {
  origin: Origin;
  request: PurchaseRequestDetailDto | null;
  existing: PurchaseOrderDetailDto | null;
}) {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = useBusinessToday();
  const o = existing?.order;
  const [supplier, setSupplier] = useState<SupplierDto | null>(null);
  const [poDate, setPoDate] = useState(o?.poDate ?? today);
  const [deliveryDate, setDeliveryDate] = useState(o?.deliveryDate ?? '');
  const [pointOfDelivery, setPointOfDelivery] = useState(o?.pointOfDelivery ?? '');
  const [contactPerson, setContactPerson] = useState(o?.contactPerson ?? '');
  const [terms, setTerms] = useState(o?.termsAndConditions ?? '');
  const [paymentTerms, setPaymentTerms] = useState(o?.paymentTerms ?? '');
  const [reason, setReason] = useState(o?.emergencyReason ?? '');
  const [lines, setLines] = useState<OrderLineDraft[]>(() =>
    request !== null ? linesFromRequest(request) : existing === null ? [emptyLine(1)] : [],
  );
  const [loading, setLoading] = useState(true);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  // The supplier (the order's, or the request's preferred one) and an existing order's lines.
  useEffect(() => {
    const run = { cancelled: false };
    void (async () => {
      const supplierId = o?.supplierId ?? request?.request.preferredSupplierId ?? null;
      const found =
        supplierId === null || !can('procurement.supplier:view')
          ? null
          : await getData(`/api/v1/procurement/suppliers/${supplierId}`, SupplierSchema).catch(
              () => null,
            );
      const rebuilt: OrderLineDraft[] = [];
      if (existing !== null) {
        for (const [index, line] of existing.lines.entries()) {
          const converted = line.purchaseRequestLineId !== null;
          rebuilt.push({
            key: index + 1,
            include: true,
            purchaseRequestLineId: line.purchaseRequestLineId,
            source:
              line.purchaseRequestLineNo === null
                ? null
                : `Request line ${String(line.purchaseRequestLineNo)}`,
            description: line.description,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            carried: converted ? carriedFrom(line) : null,
            target: converted ? emptyTarget() : await loadTarget(line, can),
          });
        }
      }
      if (!run.cancelled) {
        // Only an active supplier is selectable on an order (FRD-PRC-012).
        setSupplier(found?.status === 'active' ? found : null);
        if (existing !== null) setLines(rebuilt);
        setLoading(false);
      }
    })();
    return () => {
      run.cancelled = true;
    };
    // Loaded once per request or order version.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.request.id, o?.id, o?.version]);

  const problems = problemsOf(lines, origin);
  const complete =
    supplier !== null && poDate !== '' && (origin !== 'emergency' || reason.trim() !== '');
  const content = {
    supplierId: supplier?.id ?? '',
    poDate,
    ...(deliveryDate !== '' ? { deliveryDate } : {}),
    ...(pointOfDelivery.trim() !== '' ? { pointOfDelivery: pointOfDelivery.trim() } : {}),
    ...(contactPerson.trim() !== '' ? { contactPerson: contactPerson.trim() } : {}),
    ...(terms.trim() !== '' ? { termsAndConditions: terms.trim() } : {}),
    ...(paymentTerms.trim() !== '' ? { paymentTerms: paymentTerms.trim() } : {}),
    ...(origin === 'emergency' ? { emergencyReason: reason.trim() } : {}),
    lines: lines.filter((l) => l.include).map(linePayload),
  };
  const save = useMutation({
    mutationFn: () =>
      existing === null
        ? postJson(
            PO_API,
            {
              ...content,
              origin,
              ...(request !== null ? { purchaseRequestId: request.request.id } : {}),
            },
            PurchaseOrderDetailSchema,
            session.data?.csrfToken ?? '',
            { idempotencyKey },
          )
        : patchJson(
            `${PO_API}/${existing.order.id}`,
            content,
            PurchaseOrderDetailSchema,
            session.data?.csrfToken ?? '',
            existing.order.version,
          ),
    onSuccess: async (saved) => {
      setIdempotencyKey(newIdempotencyKey());
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      await navigate(`/procurement/purchase-orders/${saved.order.id}`);
    },
  });
  const update = (key: number, patch: Partial<OrderLineDraft>) => {
    setLines((current) => current.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };
  const nextKey = lines.reduce((max, l) => Math.max(max, l.key), 0) + 1;
  if (loading) return <LoadingState label="Preparing the order" />;

  return (
    <Box
      component="form"
      aria-label={existing === null ? 'New purchase order' : 'Edit purchase order'}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (complete && problems.length === 0 && !save.isPending) save.mutate();
      }}
    >
      <Stack spacing={3}>
        <Section title="Order">
          <Stack spacing={2} sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <OriginChip origin={origin} />
              {request !== null && (
                <Typography variant="body2">
                  From purchase request {request.request.documentNo}
                </Typography>
              )}
            </Stack>
            {origin === 'emergency' && (
              <TextField
                size="small"
                label="Reason for bypassing the purchase request process"
                required
                multiline
                minRows={2}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                }}
              />
            )}
            {can('procurement.supplier:view') ? (
              <SupplierPicker
                value={supplier}
                onChange={setSupplier}
                helperText="Only an active supplier can be chosen."
              />
            ) : (
              <Alert severity="info">
                Choosing the supplier needs permission to view suppliers.
              </Alert>
            )}
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
              }}
            >
              <TextField
                size="small"
                type="date"
                label="Order date"
                required
                value={poDate}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(e) => {
                  setPoDate(e.target.value);
                }}
              />
              <TextField
                size="small"
                type="date"
                label="Delivery date"
                value={deliveryDate}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(e) => {
                  setDeliveryDate(e.target.value);
                }}
              />
              <TextField
                size="small"
                label="Point of delivery"
                value={pointOfDelivery}
                onChange={(e) => {
                  setPointOfDelivery(e.target.value);
                }}
              />
              <TextField
                size="small"
                label="Contact person"
                value={contactPerson}
                onChange={(e) => {
                  setContactPerson(e.target.value);
                }}
              />
            </Box>
            <TextField
              size="small"
              label="Payment terms"
              value={paymentTerms}
              onChange={(e) => {
                setPaymentTerms(e.target.value);
              }}
            />
            <TextField
              size="small"
              label="Terms and conditions"
              multiline
              minRows={2}
              value={terms}
              onChange={(e) => {
                setTerms(e.target.value);
              }}
            />
          </Stack>
        </Section>
        <Section title="Lines">
          <Stack spacing={2} sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              VAT is determined by the tax configuration when the order is saved.
            </Typography>
            {lines.length === 0 && (
              <Alert severity="info">Nothing is left to order on this purchase request.</Alert>
            )}
            {lines.map((line, index) => {
              const n = String(index + 1);
              return (
                <Box
                  key={line.key}
                  role="group"
                  aria-label={`Order line ${n}`}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
                >
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
                  >
                    {line.purchaseRequestLineId !== null ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={line.include}
                            onChange={(e) => {
                              update(line.key, { include: e.target.checked });
                            }}
                          />
                        }
                        label={line.source ?? `Line ${n}`}
                      />
                    ) : (
                      <Typography variant="subtitle2">Line {n}</Typography>
                    )}
                    {lines.length > 1 && line.purchaseRequestLineId === null && (
                      <IconButton
                        size="small"
                        aria-label={`Remove line ${n}`}
                        onClick={() => {
                          setLines((current) => current.filter((l) => l.key !== line.key));
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                  {line.include && (
                    <Stack spacing={2}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          size="small"
                          label="Description"
                          required
                          value={line.description}
                          sx={{ flexGrow: 1 }}
                          onChange={(e) => {
                            update(line.key, { description: e.target.value });
                          }}
                        />
                        <TextField
                          size="small"
                          label="Quantity"
                          required
                          value={line.quantity}
                          onChange={(e) => {
                            update(line.key, { quantity: e.target.value });
                          }}
                        />
                        <TextField
                          size="small"
                          label="Unit price (AED)"
                          required
                          value={line.unitPrice}
                          onChange={(e) => {
                            update(line.key, { unitPrice: e.target.value });
                          }}
                        />
                      </Stack>
                      {line.carried !== null ? (
                        <Typography variant="body2" color="text.secondary">
                          Account {line.carried.accountLabel ?? line.carried.accountId} ·{' '}
                          {line.carried.dimensions} (from the purchase request line)
                        </Typography>
                      ) : (
                        <TargetFields
                          target={line.target}
                          onChange={(target) => {
                            update(line.key, { target });
                          }}
                        />
                      )}
                    </Stack>
                  )}
                </Box>
              );
            })}
            {origin === 'emergency' && (
              <Box>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setLines((current) => [...current, emptyLine(nextKey)]);
                  }}
                >
                  Add line
                </Button>
              </Box>
            )}
          </Stack>
        </Section>
        {problems.length > 0 && (
          <Alert severity="info" variant="outlined">
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Alert>
        )}
        {save.isError && (
          <Alert severity="error" variant="outlined">
            {save.error.message}
            {save.error instanceof ApiError && save.error.fieldErrors.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {save.error.fieldErrors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!complete || problems.length > 0 || save.isPending}
          >
            {save.isPending ? 'Saving…' : existing === null ? 'Save as draft' : 'Save changes'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
